<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button } from '@drama/ui'
import FormField from './NovelFormField.vue'
import { IMAGE_ACCEPT, useImageUpload } from '../../composables/novel/useImageUpload.js'

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, required: true },
  required: { type: Boolean, default: false },
  clearable: { type: Boolean, default: true },
  tag: { type: String, required: true },
  upload: { type: Function, required: true },
  /** 图属于谁（人物名、地点名…）：预览的替代文本与上传按钮的可访问名。 */
  alt: { type: String, default: '' },
  invalid: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()
const fileInput = ref(null)
const { uploading, error_message, uploadPicked } = useImageUpload({ upload: file => props.upload(file), tag: () => props.tag })

async function pick(event) {
  const public_url = await uploadPicked(event)
  if (public_url) emit('update:modelValue', public_url)
}
</script>

<template>
  <div
    class="novel-image-field"
    :class="{ 'has-error': invalid }"
  >
    <div class="novel-image-field__preview">
      <img
        v-if="modelValue"
        :src="modelValue"
        :alt="alt || label"
      >
      <span v-else>{{ t('novelStudio.fields.imageEmpty') }}</span>
    </div>
    <div class="novel-image-field__controls">
      <FormField
        :label="label"
        :required="required"
      >
        <span
          v-if="modelValue"
          class="novel-image-field__registered"
        >{{ t('novelAuthoring.registeredImage') }}</span>
        <span v-else>{{ t('novelStudio.fields.imageEmpty') }}</span>
      </FormField>
      <Button
        variant="secondary"
        size="sm"
        :loading="uploading"
        :aria-invalid="invalid || undefined"
        :aria-label="t('novelStudio.fields.uploadFor', { name: alt || label })"
        :data-testid="`novel-upload-${tag}`"
        @click="fileInput?.click()"
      >
        {{ t('novelStudio.fields.upload') }}
      </Button>
      <Button
        v-if="modelValue && clearable"
        variant="ghost"
        size="sm"
        @click="emit('update:modelValue', '')"
      >
        {{ t('novelStudio.fields.remove') }}
      </Button>
      <input
        ref="fileInput"
        class="novel-image-field__file"
        type="file"
        :accept="IMAGE_ACCEPT"
        @change="pick"
      >
      <p
        v-if="error_message"
        class="novel-image-field__error"
        role="alert"
      >
        {{ error_message }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.novel-image-field {
  display: grid;
  grid-template-columns: calc(var(--drama-space-xl) * 4) 1fr;
  gap: var(--drama-space-md);
  align-items: start;
}

.novel-image-field__preview {
  display: grid;
  place-items: center;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  border: 1px dashed var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  text-align: center;
}

.novel-image-field__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.novel-image-field.has-error .novel-image-field__preview {
  border-color: var(--drama-color-danger);
}

.novel-image-field__controls {
  display: grid;
  gap: var(--drama-space-sm);
  justify-items: start;
}

.novel-image-field__controls > :first-child {
  width: 100%;
}

.novel-image-field__file {
  display: none;
}
.novel-image-field__error { margin: 0; color: var(--drama-color-danger); font-size: var(--drama-font-size-sm); overflow-wrap: anywhere; }
@media (max-width: 600px) {
  .novel-image-field { grid-template-columns: 88px minmax(0, 1fr); }
}
</style>
